import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const StudentInternship = ({ onNavigate, userInfo }) => {

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [isCapturing, setIsCapturing] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const [locationGranted, setLocationGranted] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);

  // Student Data
  const [studentData, setStudentData] = useState({
    name: "Loading...",
    studentId: "...",
    profileImage: null
  });

  // 🎯 NEW: Automatic Internship Data
  const [internshipData, setInternshipData] = useState({
    hasActiveInternship: false,
    internship: null,
    hasSubmittedToday: false,
    todayAttendance: null,
    recentAttendance: [],
    systemInfo: {
      automaticProcessing: true,
      fixedRadius: 200,
      manualApprovalRequired: false,
      immediateResults: true
    }
  });

  // 🎯 NEW: Processing Status
  const [processingStatus, setProcessingStatus] = useState({
    isProcessing: false,
    step: '',
    result: null
  });

  // Camera and Location Permission States
  const [permissions, setPermissions] = useState({
    camera: 'prompt',
    location: 'prompt'
  });

  // Permission request attempts
  const [permissionAttempts, setPermissionAttempts] = useState({
    camera: 0,
    location: 0
  });

  // Camera states
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Distance calculation
  const [distanceFromCompany, setDistanceFromCompany] = useState(null);
  const [isWithinRadius, setIsWithinRadius] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check permissions on mount
  useEffect(() => {
    const initPermissions = async () => {
      try {
        await checkPermissions();
      } catch (error) {
        console.warn('⚠️ Permission check failed (non-blocking):', error.message);
      }
    };

    initPermissions();
  }, []);

  // Request location permission on mount
  useEffect(() => {
    const initLocation = async () => {
      try {
        await requestLocationPermission();
      } catch (error) {
        console.warn('⚠️ Initial location request failed (non-blocking):', error.message);
      }
    };

    initLocation();
  }, []);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // 🎯 NEW: Calculate distance when location changes
  useEffect(() => {
    if (currentLocation && internshipData.internship?.companyLocation?.latitude) {
      const distance = calculateDistance(
        currentLocation.latitude,
        currentLocation.longitude,
        internshipData.internship.companyLocation.latitude,
        internshipData.internship.companyLocation.longitude
      );
      setDistanceFromCompany(distance);
      setIsWithinRadius(distance <= 200); // Fixed 200m radius
    }
  }, [currentLocation, internshipData.internship]);

  // 🎯 NEW: Fetch Automatic Internship Data
  useEffect(() => {
    const fetchInternshipData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('📊 Starting API call to fetch automatic internship data...');

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          console.log('❌ No token found');
          setError('Authentication token not found. Please login again.');
          setStudentData(prev => ({
            ...prev,
            name: userInfo?.name || "Student User",
            studentId: userInfo?.id || "SVU2024001"
          }));
          setLoading(false);
          return;
        }

        try {
          const profileResponse = await fetch(`${API_LINK}/api/student/profile`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (profileResponse.ok) {
            const profileResult = await profileResponse.json();
            if (profileResult.success) {
              setStudentData({
                name: profileResult.data.name,
                studentId: profileResult.data.studentId,
                profileImage: profileResult.data.profileImage
              });
            }
          }
        } catch (profileError) {
          console.warn('⚠️ Profile fetch failed (non-blocking):', profileError.message);
          setStudentData(prev => ({
            ...prev,
            name: userInfo?.name || "Student User",
            studentId: userInfo?.id || "SVU2024001"
          }));
        }

        // 🎯 NEW: Fetch automatic internship data
        try {
          const internshipResponse = await fetch(`${API_LINK}/api/student/internship/active-with-location`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          console.log('📡 Automatic Internship Response status:', internshipResponse.status);

          if (!internshipResponse.ok) {
            if (internshipResponse.status === 401) {
              console.log('🔐 Authentication failed');
              setError('Session expired. Please login again.');
              window.location.href = '/';
              return;
            }
            throw new Error(`HTTP error! status: ${internshipResponse.status}`);
          }

          const internshipResult = await internshipResponse.json();
          console.log('📦 Automatic Internship API Response:', internshipResult);

          if (internshipResult.success && internshipResult.data) {
            setInternshipData(internshipResult.data);
            console.log('✅ Automatic internship data updated successfully');
          } else {
            console.log('❌ API returned error:', internshipResult.message);
            setError(internshipResult.message || 'Failed to fetch internship data');
          }
        } catch (internshipError) {
          console.error('💥 Error fetching automatic internship data:', internshipError);
          setError(`Failed to load internship data: ${internshipError.message}`);

          setInternshipData({
            hasActiveInternship: false,
            internship: null,
            hasSubmittedToday: false,
            todayAttendance: null,
            recentAttendance: [],
            systemInfo: {
              automaticProcessing: true,
              fixedRadius: 200,
              manualApprovalRequired: false,
              immediateResults: true
            }
          });
        }

      } catch (error) {
        console.error('💥 Error in fetchInternshipData:', error);
        setError(`Failed to load internship information: ${error.message || 'Unknown error'}`);

        if (userInfo) {
          console.log('📄 Using fallback userInfo data');
          setStudentData(prev => ({
            ...prev,
            name: userInfo.name || prev.name,
            studentId: userInfo.id || prev.studentId
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchInternshipData().catch(error => {
        console.error('🚨 Unhandled fetchInternshipData error:', error);
        setError('Failed to initialize internship data: ' + error.message);
        setLoading(false);
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [userInfo]);

  // 🎯 NEW: Calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c; // Distance in meters
    return Math.round(distance);
  };

  // Check browser permissions
  const checkPermissions = async () => {
    try {
      if (navigator.permissions) {
        const cameraPermission = await navigator.permissions.query({ name: 'camera' });
        const locationPermission = await navigator.permissions.query({ name: 'geolocation' });

        setPermissions({
          camera: cameraPermission.state,
          location: locationPermission.state
        });

        console.log('📷 Camera permission:', cameraPermission.state);
        console.log('📍 Location permission:', locationPermission.state);
      }
    } catch (error) {
      console.log('⚠️ Permission API not supported:', error);
    }
  };

  // Request location permission with better error handling
  const requestLocationPermission = () => {
    return new Promise((resolve, reject) => {
      try {
        if (!navigator.geolocation) {
          console.error('❌ Geolocation not supported');
          setLocationGranted(false);
          setPermissions(prev => ({ ...prev, location: 'denied' }));
          const error = new Error('Geolocation not supported');
          reject(error);
          return;
        }

        setPermissionAttempts(prev => ({ ...prev, location: prev.location + 1 }));

        navigator.geolocation.getCurrentPosition(
          (position) => {
            try {
              setLocationGranted(true);
              setCurrentLocation({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              });
              setPermissions(prev => ({ ...prev, location: 'granted' }));
              console.log('✅ Location granted:', position.coords);
              resolve(position);
            } catch (err) {
              console.error('❌ Error processing location:', err);
              reject(err);
            }
          },
          (error) => {
            try {
              setLocationGranted(false);
              setPermissions(prev => ({ ...prev, location: 'denied' }));
              console.error('❌ Location denied:', error);

              let errorMessage = 'Location access denied. ';
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  errorMessage += 'Please enable location access in your browser settings.';
                  break;
                case error.POSITION_UNAVAILABLE:
                  errorMessage += 'Location information is unavailable.';
                  break;
                case error.TIMEOUT:
                  errorMessage += 'Location request timed out.';
                  break;
                default:
                  errorMessage += 'An unknown error occurred.';
                  break;
              }

              console.warn('⚠️ Location error (non-blocking):', errorMessage);
              reject(error);
            } catch (err) {
              console.error('❌ Error handling location error:', err);
              reject(err);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 300000
          }
        );
      } catch (err) {
        console.error('❌ Error in requestLocationPermission:', err);
        setLocationGranted(false);
        setPermissions(prev => ({ ...prev, location: 'denied' }));
        reject(err);
      }
    });
  };





  // Start camera with improved error handling and compatibility
  const startCamera = async () => {
    try {
      setError(null);
      setShowCamera(true);
      console.log('📷 Starting camera...');

      // Stop any existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      // Check if browser supports camera
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.');
      }

      // Ensure location is available first (non-blocking)
      if (!locationGranted) {
        console.log('📍 Requesting location permission first...');
        try {
          await requestLocationPermission();
        } catch (locationError) {
          console.warn('⚠️ Location not available, but continuing with camera:', locationError.message);
        }
      }

      setPermissionAttempts(prev => ({ ...prev, camera: prev.camera + 1 }));

      // Try multiple constraint configurations for better compatibility
      const constraintOptions = [
        // Option 1: High quality with user camera (selfie mode)
        {
          video: {
            facingMode: 'user',
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 }
          },
          audio: false
        },
        // Option 2: Standard quality with user camera
        {
          video: {
            facingMode: 'user',
            width: { ideal: 640 },
            height: { ideal: 480 }
          },
          audio: false
        },
        // Option 3: Any camera with specific dimensions
        {
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 }
          },
          audio: false
        },
        // Option 4: Basic constraints
        {
          video: true,
          audio: false
        }
      ];

      let stream = null;
      let constraintsUsed = null;

      // Try each constraint option until one works
      for (let i = 0; i < constraintOptions.length; i++) {
        try {
          console.log(`📍 Trying camera constraints option ${i + 1}:`, constraintOptions[i]);
          stream = await navigator.mediaDevices.getUserMedia(constraintOptions[i]);
          constraintsUsed = constraintOptions[i];
          console.log('✅ Stream obtained:', stream);
          break;
        } catch (err) {
          console.warn(`⚠️ Camera constraints option ${i + 1} failed:`, err);
          if (i === constraintOptions.length - 1) {
            throw err; // If all options fail, throw the last error
          }
        }
      }

      if (!stream) {
        throw new Error('Unable to access camera with any configuration');
      }

      console.log('✅ Camera stream obtained with constraints:', constraintsUsed);
      console.log('🎥 Stream tracks:', stream.getTracks());

      if (videoRef.current) {
        console.log('📺 Setting up video element...');

        try {
          videoRef.current.srcObject = null;
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
          setCameraStream(stream);

          videoRef.current.autoplay = true;
          videoRef.current.playsInline = true;
          videoRef.current.muted = true;

          // Wait for metadata and then play
          const playVideo = () => {
            return new Promise((resolve, reject) => {
              videoRef.current.onloadedmetadata = async () => {
                console.log('✅ Video metadata loaded');
                try {
                  await videoRef.current.play();
                  console.log('✅ Video is now playing');
                  setHasCamera(true);
                  setIsCapturing(true);
                  setPermissions(prev => ({ ...prev, camera: 'granted' }));
                  resolve();
                } catch (playError) {
                  console.error('❌ Error playing video:', playError);
                  reject(playError);
                }
              };

              videoRef.current.onerror = (error) => {
                console.error('❌ Video element error:', error);
                reject(error);
              };

              // Timeout fallback
              setTimeout(() => {
                if (videoRef.current && videoRef.current.readyState >= 2) {
                  videoRef.current.play().then(resolve).catch(reject);
                } else {
                  reject(new Error('Video not ready after timeout'));
                }
              }, 3000);
            });
          };

          await playVideo();

          // Handle stream end
          stream.getTracks().forEach(track => {
            track.onended = () => {
              console.log('📹 Camera track ended');
              setIsCapturing(false);
              setHasCamera(false);
            };
          });

        } catch (videoSetupError) {
          console.error('❌ Error setting up video element:', videoSetupError);
          throw new Error('Failed to setup video element: ' + videoSetupError.message);
        }
      } else {
        throw new Error('Video element not found');
      }

    } catch (err) {
      console.error('❌ Error accessing camera:', err);

      let errorMessage = 'Failed to access camera. ';

      if (err.name === 'NotAllowedError') {
        errorMessage += 'Camera permission was denied. Please click the camera icon in your browser\'s address bar and allow camera access, then try again.';
        setPermissions(prev => ({ ...prev, camera: 'denied' }));
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'No camera found on this device. Please ensure your device has a working camera.';
      } else if (err.name === 'NotReadableError') {
        errorMessage += 'Camera is being used by another application. Please close other apps using the camera and try again.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage += 'Camera constraints cannot be satisfied. Trying with basic settings...';
        setTimeout(() => {
          startCamera().catch(retryError => {
            console.error('❌ Retry failed:', retryError);
            setError('Camera retry failed: ' + retryError.message);
          });
        }, 1000);
        return;
      } else if (err.name === 'SecurityError') {
        errorMessage += 'Camera access blocked due to security restrictions. Please ensure you\'re using HTTPS.';
      } else {
        errorMessage += err.message || 'Unknown camera error occurred.';
      }

      setError(errorMessage);
      setIsCapturing(false);
      setHasCamera(false);
      setShowCamera(false);
      stopCamera();
    }
  };

  // Capture photo function (improved from TeacherAttendance)
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      setError('Camera not ready. Please try again.');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Check if video is playing
    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      setError('Camera not ready. Please wait a moment and try again.');
      return;
    }

    try {
      const context = canvas.getContext('2d');

      // Set canvas dimensions to video dimensions
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      console.log('📸 Capturing photo:', canvas.width, 'x', canvas.height);

      // Draw the video frame to the canvas (mirrored for selfie)
      context.scale(-1, 1);
      context.translate(-canvas.width, 0);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Reset transformation for overlay
      context.setTransform(1, 0, 0, 1, 0, 0);

      // Add timestamp and location overlay
      const overlayHeight = Math.min(80, canvas.height * 0.2);
      context.fillStyle = 'rgba(0, 0, 0, 0.8)';
      context.fillRect(0, canvas.height - overlayHeight, canvas.width, overlayHeight);

      context.fillStyle = 'white';
      context.font = `bold ${Math.max(12, canvas.width * 0.025)}px Arial`;
      context.textAlign = 'left';

      const now = new Date();
      const timestamp = now.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      const locationText = currentLocation ?
        `📍 ${currentLocation.latitude.toFixed(6)}, ${currentLocation.longitude.toFixed(6)}` :
        '📍 Location: Not available';

      const textY1 = canvas.height - overlayHeight + overlayHeight * 0.4;
      const textY2 = canvas.height - overlayHeight + overlayHeight * 0.8;

      context.fillText(`📅 ${timestamp}`, 10, textY1);
      context.fillText(locationText, 10, textY2);

      // Convert canvas to blob with higher quality
      canvas.toBlob((blob) => {
        if (blob && blob.size > 0) {
          setCapturedPhoto(blob);
          stopCamera();
          console.log(`📦 Photo captured successfully, size: ${blob.size} bytes`);
        } else {
          setError('Failed to capture photo. Please try again.');
        }
      }, 'image/jpeg', 0.9);

    } catch (error) {
      console.error('Error capturing photo:', error);
      setError('Error capturing photo: ' + error.message);
    }
  };

  // Stop camera
  const stopCamera = () => {
    console.log('🛑 Stopping camera...');

    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log('🔴 Stopped track:', track.kind);
        });
        streamRef.current = null;
      }

      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    } catch (error) {
      console.warn('⚠️ Error stopping camera:', error);
    }

    setIsCapturing(false);
    setHasCamera(false);
    setShowCamera(false);
  };

  // 🎯 NEW: Upload photo function with automatic processing
  const uploadPhoto = async () => {
    if (!capturedPhoto) {
      setError('Please capture a photo first.');
      return;
    }

    if (!locationGranted || !currentLocation) {
      setError('Location access is required for automatic internship attendance verification.');
      return;
    }

    if (!internshipData.hasActiveInternship) {
      setError('No active internship found. Please contact your coordinator.');
      return;
    }

    try {
      setUploadingPhoto(true);
      setError(null);
      setUploadSuccess(null);

      // 🎯 NEW: Set processing status
      setProcessingStatus({
        isProcessing: true,
        step: 'Uploading photo and verifying location...',
        result: null
      });

      console.log('📤 Uploading automatic internship attendance photo...');

      const formData = new FormData();
      const photoFile = new File([capturedPhoto], `internship_attendance_${Date.now()}.jpg`, {
        type: 'image/jpeg'
      });
      formData.append('attendancePhoto', photoFile);
      formData.append('internshipId', internshipData.internship.id.toString());
      formData.append('latitude', currentLocation.latitude.toString());
      formData.append('longitude', currentLocation.longitude.toString());
      formData.append('locationName', 'Internship Location');
      formData.append('timestamp', new Date().toISOString());

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch(`${API_LINK}/api/student/internship/attendance-with-location`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      console.log('📡 Upload response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Automatic processing completed:', result);

        // 🎯 NEW: Handle automatic processing results
        setProcessingStatus({
          isProcessing: false,
          step: 'Processing completed',
          result: result.data
        });

        // Enhanced success message with automatic processing details
        if (result.data?.locationVerification) {
          const locationData = result.data.locationVerification;
          const classData = result.data.classAttendance;

          let detailMessage = `🎯 AUTOMATIC PROCESSING COMPLETED!\n\n`;

          if (locationData.verified) {
            detailMessage += `✅ LOCATION VERIFIED\n`;
            detailMessage += `📍 Distance: ${locationData.distanceFromCompany}m (within ${locationData.allowedRadius}m)\n`;
            detailMessage += `🎯 Result: AUTOMATICALLY APPROVED\n`;
            detailMessage += `📚 Class Status: PRESENT for all classes today\n`;
          } else {
            detailMessage += `❌ LOCATION FAILED\n`;
            detailMessage += `📍 Distance: ${locationData.distanceFromCompany}m (outside ${locationData.allowedRadius}m)\n`;
            detailMessage += `🎯 Result: AUTOMATICALLY REJECTED\n`;
            detailMessage += `📚 Class Status: ABSENT for all classes today\n`;
          }

          if (classData?.processed) {
            detailMessage += `\n⚡ IMMEDIATE PROCESSING:\n`;
            detailMessage += `• Classes affected: ${classData.classesAffected}\n`;
            detailMessage += `• No manual approval required\n`;
            detailMessage += `• Teachers cannot override this decision\n`;
          }

          setUploadSuccess(detailMessage);
        } else {
          setUploadSuccess(result.message || 'Attendance processed automatically!');
        }

        // Clear captured photo
        setCapturedPhoto(null);

        // Refresh internship data after processing
        setTimeout(() => {
          window.location.reload();
        }, 5000);

      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Upload failed:', errorData);

        setProcessingStatus({
          isProcessing: false,
          step: 'Processing failed',
          result: null
        });

        let errorMessage = 'Failed to process internship attendance. ';
        if (response.status === 401) {
          errorMessage += 'Session expired. Please login again.';
          window.location.href = '/';
          return;
        } else if (response.status === 413) {
          errorMessage += 'Photo file too large. Please try again.';
        } else {
          errorMessage += errorData.message || `Server error (${response.status})`;
        }

        setError(errorMessage);
      }
    } catch (uploadError) {
      console.error('💥 Upload error:', uploadError);

      setProcessingStatus({
        isProcessing: false,
        step: 'Upload failed',
        result: null
      });

      setError(`Failed to process automatic attendance: ${uploadError.message}. Please try again.`);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Retake photo function
  const retakePhoto = () => {
    setCapturedPhoto(null);
    setProcessingStatus({ isProcessing: false, step: '', result: null });
    startCamera();
  };

  // Request permissions manually
  const requestPermissions = async () => {
    setError(null);

    try {
      try {
        await requestLocationPermission();
        console.log('✅ Location permission granted');
      } catch (locationError) {
        console.warn('⚠️ Location permission failed (non-blocking):', locationError.message);
      }

      try {
        await startCamera();
        console.log('✅ Camera permission granted');
      } catch (cameraError) {
        console.error('❌ Camera permission failed:', cameraError.message);
      }
    } catch (error) {
      console.error('❌ Permission request failed:', error);
      setError('Failed to request permissions: ' + error.message);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get user initials
  const getUserInitials = (name) => {
    if (!name) return 'ST';
    const names = name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get profile image URL
  const getProfileImageUrl = () => {
    if (studentData.profileImage) {
      return `${API_LINK}${studentData.profileImage}`;
    }
    return null;
  };

  // Check if already marked attendance today
  const hasMarkedToday = () => {
    return internshipData.hasSubmittedToday;
  };

  // Get permission status color
  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'granted': return '#28a745';
      case 'denied': return '#dc3545';
      default: return '#ffc107';
    }
  };

  // Get permission status text
  const getPermissionText = (permission) => {
    switch (permission) {
      case 'granted': return 'Granted';
      case 'denied': return 'Denied';
      default: return 'Not Asked';
    }
  };

  // 🎯 NEW: Get status color for automatic processing
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#28a745';
      case 'rejected': return '#dc3545';
      default: return '#ffc107';
    }
  };

  // 🎯 NEW: Get status text for automatic processing
  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'AUTOMATICALLY APPROVED';
      case 'rejected': return 'AUTOMATICALLY REJECTED';
      default: return 'PROCESSING';
    }
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Automatic Internship Attendance System...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component (when no internship found)
  if (!loading && !internshipData.hasActiveInternship) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff9800',
            marginBottom: '1.5rem'
          }}>
            🏢
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            No Active Internship Found
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            You don't have an active internship registered in the automatic processing system. Please contact your internship coordinator.
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Refresh
            </button>

            <button
              onClick={() => onNavigate('student-dashboard')}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="student"
      onNavigate={onNavigate}
      activePage="student-internship"
      pageTitle="Internship Attendance"
    >
      {/* 🎯 NEW: Automatic Processing Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
          color: '#ffffff',
          padding: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>🎯</span>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.4rem' }}>Automatic Internship Attendance</h2>
              {internshipData.internship && (
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                  {internshipData.internship.companyName} - {internshipData.internship.position}
                </p>
              )}
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center'
          }}>
            <div style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: '500',
              backgroundColor: hasMarkedToday() ? '#28a745' : '#dc3545',
              color: '#ffffff'
            }}>
              {hasMarkedToday() ? '✅ Today: Processed' : '⏳ Today: Pending'}
            </div>
            <div style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.8rem',
              fontWeight: '500',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff'
            }}>
              ⚡ Automatic
            </div>
          </div>
        </div>

        {/* 🎯 NEW: Real-time Location & Distance Status */}
        {internshipData.internship?.companyLocation && (
          <div style={{
            background: isWithinRadius ? '#28a745' : '#dc3545',
            color: '#ffffff',
            padding: '1rem 1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '1rem'
          }}>
            <span>
              {currentLocation && distanceFromCompany !== null
                ? `📍 Distance from ${internshipData.internship.companyName}: ${distanceFromCompany}m (Allowed: 200m)`
                : '📡 Getting location data...'
              }
            </span>
            <span style={{
              fontSize: '0.9rem',
              opacity: 0.8,
              fontWeight: 'bold'
            }}>
              {isWithinRadius ? '✅ WILL AUTO-APPROVE' : '❌ WILL AUTO-REJECT'}
            </span>
          </div>
        )}
      </div>

      {/* Error/Success Messages */}
      {(error || uploadSuccess) && (
        <div style={{
          background: uploadSuccess ? '#d4edda' : '#f8d7da',
          border: `1px solid ${uploadSuccess ? '#c3e6cb' : '#f5c6cb'}`,
          color: uploadSuccess ? '#155724' : '#721c24',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.5rem',
          whiteSpace: 'pre-line'
        }}>
          <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>
            {uploadSuccess ? '🎯' : '❌'}
          </span>
          <div style={{ flex: 1 }}>
            {uploadSuccess || error}
          </div>
          <button
            onClick={() => {
              setError(null);
              setUploadSuccess(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: '1.2rem',
              padding: '0.25rem',
              flexShrink: 0
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* 🎯 NEW: Processing Status */}
      {processingStatus.isProcessing && (
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          color: '#856404',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #856404',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span>⚡ {processingStatus.step}</span>
        </div>
      )}

      {/* Permissions Status */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <h3 style={{ margin: 0, color: '#333', fontSize: '1.1rem' }}>
            📱 Device Permissions & Automatic Location Status
          </h3>
          {(permissions.camera === 'denied' || permissions.location === 'denied') && (
            <button
              onClick={requestPermissions}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              🔄 Request Permissions
            </button>
          )}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: '1rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: `2px solid ${getPermissionColor(permissions.camera)}`
          }}>
            <div>
              <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>📷</span>
              <span style={{ fontWeight: '500' }}>Camera</span>
            </div>
            <span style={{
              color: getPermissionColor(permissions.camera),
              fontWeight: '600'
            }}>
              {getPermissionText(permissions.camera)}
            </span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: `2px solid ${getPermissionColor(permissions.location)}`
          }}>
            <div>
              <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>📍</span>
              <span style={{ fontWeight: '500' }}>Location</span>
            </div>
            <span style={{
              color: getPermissionColor(permissions.location),
              fontWeight: '600'
            }}>
              {getPermissionText(permissions.location)}
            </span>
          </div>
        </div>

        {/* 🎯 NEW: Real-time Automatic Processing Info */}
        {locationGranted && currentLocation && internshipData.internship?.companyLocation && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: isWithinRadius ? '#d4edda' : '#f8d7da',
            border: `1px solid ${isWithinRadius ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '8px',
            fontSize: '0.9rem'
          }}>
            <div style={{
              color: isWithinRadius ? '#155724' : '#721c24',
              fontWeight: '600',
              marginBottom: '0.5rem'
            }}>
              🎯 Automatic Processing Preview
            </div>
            <div style={{
              color: isWithinRadius ? '#155724' : '#721c24',
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '0.5rem 1rem',
              fontSize: '0.85rem'
            }}>
              <span>Your Location:</span>
              <span>{currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}</span>
              <span>Company Location:</span>
              <span>{internshipData.internship.companyLocation.latitude.toFixed(6)}, {internshipData.internship.companyLocation.longitude.toFixed(6)}</span>
              <span>Distance:</span>
              <span>{distanceFromCompany}m</span>
              <span>Fixed Radius:</span>
              <span>200m (unchangeable)</span>
              <span>Auto Decision:</span>
              <span style={{ fontWeight: '600' }}>
                {isWithinRadius ? '🎯 WILL APPROVE → PRESENT for all classes' : '🎯 WILL REJECT → ABSENT for all classes'}
              </span>
              <span>Processing:</span>
              <span style={{ fontWeight: '600' }}>
                ⚡ IMMEDIATE (no manual approval required)
              </span>
            </div>
          </div>
        )}

        {/* Permission Instructions */}
        {(permissions.camera === 'denied' || permissions.location === 'denied') && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '8px',
            fontSize: '0.9rem',
            color: '#856404'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
              📋 To enable automatic processing:
            </div>
            <div style={{ marginBottom: '0.25rem' }}>
              1. Click the lock/camera icon in your browser's address bar
            </div>
            <div style={{ marginBottom: '0.25rem' }}>
              2. Select "Allow" for Camera and Location
            </div>
            <div>
              3. Refresh the page or click "Request Permissions" above
            </div>
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '1.5rem'
      }}>
        {/* 🎯 NEW: Automatic Attendance Submission */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>⚡</span>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Automatic Attendance Processing</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <p style={{
              margin: '0 0 1rem 0',
              color: '#666',
              fontSize: '0.95rem',
              textAlign: 'center'
            }}>
              Take a selfie at your internship location. The system will automatically verify your location within 200m radius and instantly approve/reject your attendance for all classes.
            </p>

            {/* Camera Area */}
            <div style={{
              border: '2px dashed #97d6ac',
              borderRadius: '8px',
              padding: '1rem',
              textAlign: 'center',
              backgroundColor: '#000',
              minHeight: '300px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {showCamera ? (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    controls={false}
                    style={{
                      width: '100%',
                      height: '250px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      transform: 'scaleX(-1)', // Mirror for selfie
                      backgroundColor: '#000',
                      display: 'block'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    border: '2px dashed #fff',
                    width: '200px',
                    height: '150px',
                    borderRadius: '8px',
                    pointerEvents: 'none'
                  }} />
                  <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '10px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px'
                  }}>
                    📍 Location: {currentLocation ?
                      `${currentLocation.latitude.toFixed(4)}, ${currentLocation.longitude.toFixed(4)}` :
                      'Getting location...'
                    }
                  </div>
                  {distanceFromCompany !== null && (
                    <div style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                      color: '#fff',
                      fontSize: '0.8rem',
                      backgroundColor: isWithinRadius ? 'rgba(40, 167, 69, 0.8)' : 'rgba(220, 53, 69, 0.8)',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px'
                    }}>
                      🎯 {distanceFromCompany}m {isWithinRadius ? '✅' : '❌'}
                    </div>
                  )}
                </>
              ) : capturedPhoto ? (
                <>
                  <div style={{
                    width: '100%',
                    maxWidth: '300px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginBottom: '15px'
                  }}>
                    <img
                      src={URL.createObjectURL(capturedPhoto)}
                      alt="Captured attendance"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </div>
                  <p style={{
                    color: '#fff',
                    fontSize: '0.9rem',
                    margin: '0 0 1rem 0'
                  }}>
                    Photo captured successfully! Ready for automatic processing.
                  </p>
                  {distanceFromCompany !== null && (
                    <p style={{
                      color: isWithinRadius ? '#28a745' : '#dc3545',
                      fontSize: '0.8rem',
                      margin: '0',
                      fontWeight: '600'
                    }}>
                      🎯 Distance: {distanceFromCompany}m - Will be {isWithinRadius ? 'AUTOMATICALLY APPROVED' : 'AUTOMATICALLY REJECTED'}
                    </p>
                  )}
                </>
              ) : (
                <div style={{
                  color: '#fff',
                  fontSize: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{
                    border: '2px dashed #fff',
                    width: '200px',
                    height: '150px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '1rem'
                  }}>
                    <span>Position your face within the frame at your internship location for automatic processing</span>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              {hasMarkedToday() ? (
                <button
                  disabled
                  style={{
                    background: internshipData.todayAttendance?.status === 'approved' ? '#28a745' : '#dc3545',
                    color: '#ffffff',
                    border: 'none',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    cursor: 'not-allowed',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  {internshipData.todayAttendance?.status === 'approved' ? '✅ AUTOMATICALLY APPROVED' : '❌ AUTOMATICALLY REJECTED'}
                </button>
              ) : showCamera ? (
                <>
                  <button
                    onClick={stopCamera}
                    disabled={uploadingPhoto}
                    style={{
                      background: '#6c757d',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      opacity: uploadingPhoto ? 0.7 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={capturePhoto}
                    disabled={uploadingPhoto}
                    style={{
                      background: uploadingPhoto ? '#ccc' : '#238548',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      opacity: uploadingPhoto ? 0.7 : 1
                    }}
                  >
                    📸 Capture Photo
                  </button>
                </>
              ) : capturedPhoto ? (
                <>
                  <button
                    onClick={retakePhoto}
                    disabled={uploadingPhoto}
                    style={{
                      background: '#ff9800',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      opacity: uploadingPhoto ? 0.7 : 1
                    }}
                  >
                    🔄 Retake
                  </button>
                  <button
                    onClick={uploadPhoto}
                    disabled={uploadingPhoto}
                    style={{
                      background: uploadingPhoto ? '#ccc' : isWithinRadius ? '#28a745' : '#dc3545',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      opacity: uploadingPhoto ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {uploadingPhoto ? (
                      <>
                        <div style={{
                          width: '14px',
                          height: '14px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderRadius: '50%',
                          borderTop: '2px solid white',
                          animation: 'spin 1s infinite linear'
                        }} />
                        Auto-Processing...
                      </>
                    ) : (
                      <>⚡ Submit & Auto-Process</>
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={startCamera}
                  disabled={!locationGranted}
                  style={{
                    background: !locationGranted ? '#ccc' : '#238548',
                    color: '#ffffff',
                    border: 'none',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    cursor: !locationGranted ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'background-color 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    if (locationGranted) {
                      e.target.style.backgroundColor = '#1e7040';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (locationGranted) {
                      e.target.style.backgroundColor = '#238548';
                    }
                  }}
                >
                  📷 Start Camera
                </button>
              )}
            </div>

            {/* Current Location Info */}
            <div style={{
              background: locationGranted ? '#d4edda' : '#fff3cd',
              border: `1px solid ${locationGranted ? '#c3e6cb' : '#ffeaa7'}`,
              borderRadius: '6px',
              padding: '0.75rem',
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{
                color: locationGranted ? '#155724' : '#856404',
                fontSize: '1rem'
              }}>
                {locationGranted ? '⚡' : '⚠️'}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{
                  color: locationGranted ? '#155724' : '#856404',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  {locationGranted
                    ? '⚡ Location access granted - Automatic processing enabled (no manual approval needed)'
                    : '⚠️ Location access required for automatic attendance processing'
                  }
                </div>
                {locationGranted && currentLocation && (
                  <div style={{
                    color: '#666',
                    fontSize: '0.8rem'
                  }}>
                    Current: {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 🎯 NEW: Automatic Processing History & Results */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>📊</span>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Automatic Processing History</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {/* Recent Attendance */}
            <h4 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1rem' }}>Recent Auto-Processed Results</h4>
            {internshipData.recentAttendance?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {internshipData.recentAttendance.slice(0, 3).map((record, index) => (
                  <div key={index} style={{
                    padding: '1rem',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: `2px solid ${getStatusColor(record.status)}`
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {formatDate(record.date)}
                      </span>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '15px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        backgroundColor: record.status === 'approved' ? '#d4edda' : '#f8d7da',
                        color: record.status === 'approved' ? '#155724' : '#721c24'
                      }}>
                        {record.status === 'approved' ? '⚡ Auto-Approved' : '⚡ Auto-Rejected'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      Time: {formatTime(record.checkInTime)} |
                      Distance: {record.distanceFromCompany}m |
                      {record.isWithinRadius ? ' ✅ Within 200m' : ' ❌ Outside 200m'} |
                      Result: {record.approvalType?.toUpperCase()}
                    </div>
                    {record.systemNotes && (
                      <div style={{
                        fontSize: '0.8rem',
                        color: '#666',
                        marginTop: '0.25rem',
                        fontStyle: 'italic'
                      }}>
                        System: {record.systemNotes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#666',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  No automatic processing history found.
                </p>
              </div>
            )}

            {/* System Information */}
            <h4 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1rem' }}>
              ⚡ System Information
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '0.5rem',
              backgroundColor: '#e8f5e8',
              border: '1px solid #c3e6cb',
              borderRadius: '8px',
              padding: '1rem',
              fontSize: '0.85rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Processing Type:</span>
                <span style={{ fontWeight: '600' }}>⚡ Fully Automatic</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Approval Required:</span>
                <span style={{ fontWeight: '600' }}>❌ None</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Fixed Radius:</span>
                <span style={{ fontWeight: '600' }}>📍 200 meters</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Results:</span>
                <span style={{ fontWeight: '600' }}>⚡ Immediate</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Teacher Override:</span>
                <span style={{ fontWeight: '600' }}>🔒 Blocked</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🎯 NEW: Today's Auto-Processing Summary */}
      {internshipData.todayAttendance && (
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          padding: '1.5rem',
          marginTop: '1.5rem'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>
            📅 Today's Automatic Processing Summary
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: '1rem'
          }}>
            <div style={{
              textAlign: 'center',
              padding: '1rem',
              backgroundColor: internshipData.todayAttendance.status === 'approved' ? '#e8f5e8' : '#f8e8e8',
              borderRadius: '8px'
            }}>
              <div style={{
                fontSize: '1.2rem',
                color: getStatusColor(internshipData.todayAttendance.status),
                fontWeight: 'bold'
              }}>
                {internshipData.todayAttendance.status === 'approved' ? '⚡ AUTO-APPROVED' : '⚡ AUTO-REJECTED'}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                Processing Result
              </div>
            </div>

            <div style={{
              textAlign: 'center',
              padding: '1rem',
              backgroundColor: '#e3f2fd',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '1.2rem', color: '#2196f3', fontWeight: 'bold' }}>
                {internshipData.todayAttendance.distanceFromCompany}m
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                Distance from Company
              </div>
            </div>

            <div style={{
              textAlign: 'center',
              padding: '1rem',
              backgroundColor: '#fff3cd',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '1.2rem', color: '#ffc107', fontWeight: 'bold' }}>
                {formatTime(internshipData.todayAttendance.checkInTime)}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                Check-in Time
              </div>
            </div>

            <div style={{
              textAlign: 'center',
              padding: '1rem',
              backgroundColor: internshipData.todayAttendance.approvalType === 'present' ? '#e8f5e8' : '#f8e8e8',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '1.2rem', color: internshipData.todayAttendance.approvalType === 'present' ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                {internshipData.todayAttendance.approvalType === 'present' ? '✅ PRESENT' : '❌ ABSENT'}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                Class Attendance
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden canvas for photo processing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Layout>
  );
};

export default StudentInternship;